import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppTranslation } from "./TranslationContext";

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import Code from '@tiptap/extension-code';
import Blockquote from '@tiptap/extension-blockquote';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';

import Editor from '@monaco-editor/react';

import s from '../Yakuniy.module.css';

const CODE_LANGUAGES = [
  { label: 'Python', value: 'python' },
  { label: 'C++', value: 'cpp' },
  { label: 'C#', value: 'csharp' },
  { label: 'C', value: 'c' },
  { label: 'Java', value: 'java' },
  { label: 'JavaScript', value: 'javascript' },
  { label: 'TypeScript', value: 'typescript' },
  { label: 'SQL', value: 'sql' },
];

const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

export const Yakuniy = () => {
  const { user, userKursData, setUserKursData } = useAuth();
  const { kursId } = useParams();
  const { t } = useAppTranslation();
  const lang = localStorage.getItem('language') || 'uz';
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  // answerContents: { [questionId]: string } — для теор. html, для практ. code
  const [answerContents, setAnswerContents] = useState({});
  // codeLangs: { [questionId]: string } — выбранный язык для практ. вопроса
  const [codeLangs, setCodeLangs] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [gruhId, setGruhId] = useState(null);
  const [oraliq, setOraliq] = useState(null);
  const [violations, setViolations] = useState(0);
  const [readyToStart, setReadyToStart] = useState(false); // ждём клика пользователя для fullscreen
  const [violationWarning, setViolationWarning] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [certLoading, setCertLoading] = useState(false);

  const [isImageModalOpen, setImageModalOpen] = useState(false);
  const [isLinkModalOpen, setLinkModalOpen] = useState(false);
  const [imageValue, setImageValue] = useState('');
  const [linkValue, setLinkValue] = useState('');

  const activeIdxRef = useRef(activeIdx);
  const questionsRef = useRef(questions);
  const answerContentsRef = useRef(answerContents);
  activeIdxRef.current = activeIdx;
  questionsRef.current = questions;
  answerContentsRef.current = answerContents;

  // Tiptap — только для теоретических
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Strike,
      Code,
      Blockquote,
      Image,
      Link.configure({ openOnClick: true }),
    ],
    content: '',
  });

  const activeQuestion = questions[activeIdx];
  const isPractical = activeQuestion && !activeQuestion.nazariy;

  const getSessionKey = useCallback(() =>
    `yakuniy_session_${kursId}_${user?.id}`, [kursId, user?.id]);

  const saveSession = useCallback((data) => {
    localStorage.setItem(getSessionKey(), JSON.stringify(data));
  }, [getSessionKey]);

  const clearSession = useCallback(() => {
    localStorage.removeItem(getSessionKey());
  }, [getSessionKey]);

  // Fullscreen
  const enterFullscreen = useCallback(() => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
  }, []);

  const exitFullscreen = useCallback(() => {
    if (document.exitFullscreen) document.exitFullscreen();
    else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
  }, []);

  // Пункт 1: Tab visibility + Пункт 3: Fullscreen change
  useEffect(() => {
    if (submitted || loading) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setViolations(prev => {
          const next = prev + 1;
          setViolationWarning(`⚠️ Siz boshqa oynaga o'tdingiz! Ogohlantirish: ${next}`);
          return next;
        });
      }
    };

    const handleFullscreenChange = () => {
      const isFull = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setIsFullscreen(isFull);
      if (!isFull) {
        setViolations(prev => {
          const next = prev + 1;
          setViolationWarning(`⚠️ To'liq ekrandan chiqdingiz! Ogohlantirish: ${next}`);
          return next;
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, [submitted, loading]);

  // Пункт 2: Запрет копирования, вставки, правой кнопки
  useEffect(() => {
    if (submitted || loading) return;

    const block = (e) => e.preventDefault();

    document.addEventListener('copy', block);
    document.addEventListener('cut', block);
    document.addEventListener('paste', block);
    document.addEventListener('contextmenu', block);

    const handleKeyDown = (e) => {
      const forbidden =
        (e.ctrlKey && ['c', 'v', 'x', 'a', 'u'].includes(e.key.toLowerCase())) ||
        (e.ctrlKey && e.shiftKey && e.key === 'I') || // DevTools
        e.key === 'F12';
      if (forbidden) e.preventDefault();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('copy', block);
      document.removeEventListener('cut', block);
      document.removeEventListener('paste', block);
      document.removeEventListener('contextmenu', block);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [submitted, loading]);

  // Авто-сохранение ответов и языков в localStorage
  useEffect(() => {
    if (submitted || questions.length === 0) return;
    const raw = localStorage.getItem(getSessionKey());
    if (!raw) return;
    try {
      const session = JSON.parse(raw);
      saveSession({ ...session, answerContents, codeLangs });
    } catch {}
  }, [answerContents, codeLangs]);

  // Загрузка данных
  useEffect(() => {
    if (!user?.id) return;

    const fetchAll = async () => {
      try {
        const localUser = JSON.parse(localStorage.getItem('user'));
        const gruhId = localUser?.gruh;
        if (!gruhId) return;

        const [gruhRes, questionsRes, answersRes] = await Promise.all([
          fetch(`https://api.aiassessment.uz/api/gruh/${gruhId}/`),
          fetch(`https://api.aiassessment.uz/api/kurs-yozma-savol/?kurs=${kursId}`),
          fetch(`https://api.aiassessment.uz/api/yozma-javob/?kurs=${kursId}&user=${user.id}`),
        ]);

        const gruh = await gruhRes.json();
        const allQuestions = await questionsRes.json();
        const existingAnswers = await answersRes.json();

        console.log('[Yakuniy] gruhId:', gruhId, typeof gruhId);
        console.log('[Yakuniy] gruh object:', gruh);
        console.log('[Yakuniy] allQuestions count:', allQuestions.length);
        console.log('[Yakuniy] allQuestions gruh sample:', JSON.stringify(allQuestions.slice(0, 3).map(q => ({ id: q.id, gruh: q.gruh, nazariy: q.nazariy }))));
        console.log('[Yakuniy] existingAnswers count:', existingAnswers.length);

        setGruhId(gruhId);
        setOraliq(gruh.oraliq);

        const grouped = allQuestions.filter(q =>
          Array.isArray(q.gruhs)
            ? q.gruhs.includes(Number(gruhId))
            : Number(q.gruh) === Number(gruhId)
        );
        const nazariy = grouped.filter(q => q.nazariy === true);
        const amaliy = grouped.filter(q => q.nazariy === false);

        console.log('[Yakuniy] grouped:', grouped.length, '| nazariy:', nazariy.length, '| amaliy:', amaliy.length);
        console.log('[Yakuniy] gruh.Nsavol:', gruh.Nsavol, '| gruh.Asavol:', gruh.Asavol);

        const shuffle = (arr) => [...arr].sort(() => 0.5 - Math.random());

        const activeAnswers = existingAnswers.filter(a => a.active !== false);
        const allDeactivated = existingAnswers.length > 0 && activeAnswers.length === 0;

        if (existingAnswers.length > 0 && !allDeactivated) {
          // Уже отправлено — показываем результаты только по активным ответам
          setSubmitted(true);
          const resMap = {};
          activeAnswers.forEach(a => {
            resMap[a.savol] = { ball: a.ball, komment: a.komment, javob: a.javob };
          });
          setResults(resMap);

          // Сохраняем оценку в urunishfoiz: сумма баллов / кол-во вопросов, округление вверх
          const gradedAnswers = activeAnswers.filter(a => a.ball !== null && a.ball !== undefined);
          if (gradedAnswers.length > 0) {
            const totalBall = gradedAnswers.reduce((sum, a) => sum + (a.ball || 0), 0);
            const urunishfoiz = Math.ceil(totalBall / activeAnswers.length);

            let ukId = userKursData?.id;
            if (!ukId) {
              try {
                const ukRes = await fetch(`https://api.aiassessment.uz/api/user-kurs/?user=${user.id}&kurs=${kursId}`);
                const ukData = await ukRes.json();
                ukId = ukData?.[0]?.id;
              } catch {}
            }
            if (ukId) {
              fetch(`https://api.aiassessment.uz/api/user-kurs/${ukId}/`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ urunishfoiz }),
              }).catch(() => {});
            }
          }

          const answeredQuestions = activeAnswers
            .map(a => allQuestions.find(q => q.id === a.savol))
            .filter(Boolean);
          const finalQuestions = answeredQuestions.length > 0 ? answeredQuestions : [];
          setQuestions(finalQuestions);

          const contents = {};
          activeAnswers.forEach(a => { contents[a.savol] = a.javob || ''; });
          setAnswerContents(contents);

          const firstQ = finalQuestions[0];
          if (editor && firstQ?.nazariy) {
            editor.commands.setContent(contents[firstQ.id] || '');
            editor.setOptions({ editable: false });
          }
          clearSession();
        } else {
          // allDeactivated = true — разрешаем пересдачу, сбрасываем сессию
          if (allDeactivated) clearSession();
          // Проверяем сохранённую сессию
          const savedRaw = localStorage.getItem(getSessionKey());
          if (savedRaw) {
            try {
              const session = JSON.parse(savedRaw);
              const remaining = Math.round((session.timerEndAt - Date.now()) / 1000);

              setQuestions(session.questions);
              setAnswerContents(session.answerContents || {});
              setCodeLangs(session.codeLangs || {});

              if (session.timerEndAt && remaining > 0) {
                setTimeLeft(remaining);
              } else if (session.timerEndAt) {
                // Время вышло пока страница была закрыта
                setTimeLeft(0);
              }

              const firstQ = session.questions[0];
              if (editor && firstQ?.nazariy) {
                editor.commands.setContent((session.answerContents || {})[firstQ.id] || '');
              }
              setReadyToStart(true); // ждём жеста пользователя для fullscreen
              return; // не продолжаем
            } catch {}
          }

          // Новая сессия — выбираем вопросы рандомно
          const selected = [
            ...shuffle(nazariy).slice(0, gruh.Nsavol),
            ...shuffle(amaliy).slice(0, gruh.Asavol),
          ];
          setQuestions(selected);

          const timerEndAt = gruh.vaqt ? Date.now() + gruh.vaqt * 60 * 1000 : null;
          if (timerEndAt) setTimeLeft(gruh.vaqt * 60);

          saveSession({ questions: selected, timerEndAt, answerContents: {}, codeLangs: {} });
          setReadyToStart(true); // показываем кнопку "Начать"
        }

      } catch (err) {
        console.error('Ошибка загрузки:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [user, kursId]);

  // Инициализируем tiptap при загрузке первого теоретического вопроса
  useEffect(() => {
    if (!editor || questions.length === 0 || submitted) return;
    const firstQ = questions[0];
    if (firstQ?.nazariy) {
      editor.commands.setContent(answerContents[firstQ.id] || '');
    }
  }, [questions.length > 0, editor]);

  // Таймер
  useEffect(() => {
    if (timeLeft === null || submitted) return;
    if (timeLeft <= 0) { handleSubmit(); return; }
    const timer = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft, submitted]); // eslint-disable-line

  // Сохранить ответ текущего вопроса перед переключением
  const saveCurrentAnswer = useCallback(() => {
    const currentQ = questionsRef.current[activeIdxRef.current];
    if (!currentQ) return;
    if (currentQ.nazariy) {
      // теоретический — берём из tiptap
      if (editor) {
        setAnswerContents(prev => ({ ...prev, [currentQ.id]: editor.getHTML() }));
      }
    }
    // практический — Monaco обновляет answerContents через onChange напрямую
  }, [editor]);

  const switchQuestion = useCallback((idx) => {
    saveCurrentAnswer();
    setActiveIdx(idx);
    const nextQ = questionsRef.current[idx];
    if (nextQ?.nazariy && editor) {
      setTimeout(() => {
        editor.commands.setContent(answerContentsRef.current[nextQ.id] || '');
      }, 0);
    }
  }, [editor, saveCurrentAnswer]);

  const handleCodeChange = useCallback((value) => {
    const currentQ = questionsRef.current[activeIdxRef.current];
    if (!currentQ) return;
    setAnswerContents(prev => ({ ...prev, [currentQ.id]: value || '' }));
  }, []);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    saveCurrentAnswer();

    // Даём React применить состояние
    await new Promise(r => setTimeout(r, 50));

    const finalContents = answerContentsRef.current;
    setIsSubmitting(true);

    try {
      for (const q of questionsRef.current) {
        let javob = finalContents[q.id] || '';
        // Для практических — оборачиваем в блок с языком
        if (!q.nazariy) {
          const lang = codeLangs[q.id] || 'cpp';
          javob = `[lang:${lang}]\n${javob}`;
        }
        await fetch('https://api.aiassessment.uz/api/yozma-javob/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ javob, kurs: kursId, savol: q.id, user: user.id, gruh: gruhId }),
        });
      }

      if (userKursData && parseInt(userKursData.kurs) === parseInt(kursId)) {
        const patchRes = await fetch(`https://api.aiassessment.uz/api/user-kurs/${userKursData.id}/`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user: user.id,
            kurs: parseInt(kursId),
            mavzu: parseInt(userKursData.mavzu, 10) + 1,
          }),
        });
        if (patchRes.ok) {
          const updated = await patchRes.json();
          setUserKursData({ ...userKursData, mavzu: updated.mavzu });
        }
      }

      setSubmitted(true);
      if (editor) editor.setOptions({ editable: false });
      clearSession();
      exitFullscreen();
      setShowModal(true);
    } catch (err) {
      console.error('Ошибка отправки:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGetCertificate = async () => {
    setCertLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Token ${token}` } : {}),
      };

      // Get user-kurs id
      let uk = userKursData;
      if (!uk?.id) {
        const res = await fetch(`https://api.aiassessment.uz/api/user-kurs/?user=${user?.id}&kurs=${kursId}`, { headers });
        const data = await res.json();
        uk = data?.[0];
      }
      if (!uk?.id) throw new Error('user-kurs not found');

      // 1) Allow certificate
      const patchRes = await fetch(`https://api.aiassessment.uz/api/user-kurs/${uk.id}/`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ sertifikatga_ruxsat: true }),
      });
      if (!patchRes.ok) throw new Error('Failed to allow certificate');

      // 2) Generate and get URL
      const certRes = await fetch(`https://api.aiassessment.uz/api/generate-certificate/${uk.id}/`, { headers });
      if (!certRes.ok) throw new Error(`Certificate request failed: ${certRes.status}`);

      const data = await certRes.json();
      let url = data?.sertifikat_url;
      if (!url) throw new Error('Certificate URL not found in response');

      if (!/^https?:\/\//i.test(url)) {
        url = `https://api.aiassessment.uz${url.startsWith('/') ? '' : '/'}${url}`;
      }

      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error(err.message);
      alert(`Xatolik: ${err.message}`);
    } finally {
      setCertLoading(false);
    }
  };

  const getSavolText = (q) => {
    if (lang === 'ru') return q?.savol_ru;
    if (lang === 'en') return q?.savol_en;
    return q?.savol_uz;
  };

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const ss = sec % 60;
    return `${m}:${ss < 10 ? '0' : ''}${ss}`;
  };

  const MenuBar = () => {
    if (!editor) return null;
    return (
      <div className={s.menuBar}>
        <button onClick={() => editor.chain().focus().toggleBold().run()}><strong>B</strong></button>
        <button onClick={() => editor.chain().focus().toggleItalic().run()}><i>I</i></button>
        <button onClick={() => editor.chain().focus().toggleUnderline().run()}><u>U</u></button>
        <button onClick={() => editor.chain().focus().toggleStrike().run()}><s>S</s></button>
        <button onClick={() => editor.chain().focus().toggleCode().run()}>{'</>'}</button>
        <button onClick={() => editor.chain().focus().toggleBlockquote().run()}>❝</button>
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>H1</button>
        <button onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</button>
        <button onClick={() => editor.chain().focus().toggleBulletList().run()}>•</button>
        <button onClick={() => editor.chain().focus().toggleOrderedList().run()}>1.</button>
        <button onClick={() => editor.chain().focus().unsetAllMarks().run()}>{t('clear')}</button>
        <button onClick={() => setImageModalOpen(true)}>🖼️</button>
        <button onClick={() => setLinkModalOpen(true)}>🔗</button>
        <button onClick={() => editor.chain().focus().unsetLink().run()}>❌</button>
      </div>
    );
  };

  const resultValues = Object.values(results);
  const totalBall = resultValues.reduce((sum, r) => sum + (r.ball || 0), 0);
  const questionCount = resultValues.filter(r => r.ball !== null && r.ball !== undefined).length;
  const percent = questionCount > 0 ? Math.round(totalBall / questionCount) : 0;
  const finalBall = oraliq !== null ? Math.ceil(oraliq * percent / 100) : null;

  if (loading) return <p className="container">Yuklanmoqda...</p>;

  if (isMobile) return (
    <div style={{
      background: '#111',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 24px',
      textAlign: 'center',
    }}>
      <div>
        <div style={{
          fontSize: '56px',
          marginBottom: '24px',
          lineHeight: 1,
        }}>🖥️</div>
        <div style={{
          display: 'inline-block',
          background: '#C8FF57',
          color: '#111',
          fontSize: '11px',
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '3px',
          padding: '6px 16px',
          borderRadius: '100px',
          marginBottom: '24px',
        }}>
          Yakuniy nazorat
        </div>
        <h1 style={{
          fontSize: '32px',
          fontWeight: 900,
          color: '#fff',
          letterSpacing: '-1px',
          lineHeight: 1.15,
          margin: '0 0 16px',
        }}>
          Telefon orqali<br />kirish mumkin emas
        </h1>
        <p style={{
          fontSize: '16px',
          color: 'rgba(255,255,255,0.5)',
          lineHeight: 1.7,
          maxWidth: '320px',
          margin: '0 auto',
        }}>
          Yakuniy nazoratni faqat kompyuter yoki noutbukdan topshirish mumkin.
        </p>
      </div>
    </div>
  );

  return (
    <div className={s.pageWrapper}>
      <h2 className={s.title}>{t('yakuniyTitle')}</h2>

      {timeLeft !== null && !submitted && (
        <div className={`${s.timer} ${timeLeft < 60 ? s.timerWarning : ''}`}>
          ⏱ {formatTime(timeLeft)}
        </div>
      )}

      {violationWarning && !submitted && (
        <div className={s.violationBanner}>
          {violationWarning}
          {!isFullscreen && (
            <button className={s.fullscreenBtn} onClick={enterFullscreen}>
              🖥 To'liq ekranga qaytish
            </button>
          )}
        </div>
      )}

      {readyToStart && !submitted && (
        <div className={s.startScreen}>
          <div className={s.startCard}>
            <h3>Imtihonni boshlashga tayyormisiz?</h3>
            <p>Imtihon to'liq ekran rejimida o'tkaziladi. Boshqa oynaga o'tish va ko'chirish taqiqlanadi.</p>
            <button
              className={s.startBtn}
              onClick={() => {
                enterFullscreen();
                setReadyToStart(false);
              }}
            >
              Imtihonni boshlash
            </button>
          </div>
        </div>
      )}

      <div className={s.columns}>

        {/* Колонка 1: список вопросов */}
        <div className={s.colQuestions}>
          <h3 className={s.colTitle}>{t('question') || 'Вопросы'}</h3>
          {questions.length === 0 && <p>Вопросов нет.</p>}
          {questions.map((q, idx) => (
            <div
              key={q.id}
              className={`${s.questionItem} ${idx === activeIdx ? s.active : ''} ${answerContents[q.id] ? s.answered : ''}`}
              onClick={() => switchQuestion(idx)}
            >
              <span className={`${s.questionBadge} ${q.nazariy ? s.badgeTheory : s.badgePractice}`}>
                {q.nazariy ? (
                  // Книга — теория
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                    <path d="M21 4H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1zm-1 15H7a1 1 0 0 1 0-2h13v1a1 1 0 0 1 0 1zM5 6a1 1 0 0 1 1-1h13v11H7a3 3 0 0 0-2 .78V6zM3 18V6a1 1 0 0 0-2 0v12a3 3 0 0 0 3 3h14a1 1 0 0 0 0-2H4a1 1 0 0 1-1-1z"/>
                  </svg>
                ) : (
                  // Терминал — практика
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                    <path d="M20 3H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm0 16H4V5h16v14zM6.293 9.707 9.586 13l-3.293 3.293 1.414 1.414L12.414 13 7.707 8.293zM13 15h4v2h-4z"/>
                  </svg>
                )}
              </span>
              <div className={s.questionMeta}>
                <span className={s.questionNum}>#{idx + 1}</span>
                <p className={s.questionPreview}>{getSavolText(q)?.slice(0, 55)}…</p>
              </div>
            </div>
          ))}
        </div>

        {/* Колонка 2: редактор */}
        <div className={s.colEditor}>
          {questions.length > 0 ? (
            <>
              <div className={s.questionFull}>
                <span className={`${s.questionType} ${isPractical ? s.typePractice : s.typeTheory}`}>
                  {isPractical ? (
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
                      <path d="M20 3H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm0 16H4V5h16v14zM6.293 9.707 9.586 13l-3.293 3.293 1.414 1.414L12.414 13 7.707 8.293zM13 15h4v2h-4z"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
                      <path d="M21 4H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1zm-1 15H7a1 1 0 0 1 0-2h13v1a1 1 0 0 1 0 1zM5 6a1 1 0 0 1 1-1h13v11H7a3 3 0 0 0-2 .78V6zM3 18V6a1 1 0 0 0-2 0v12a3 3 0 0 0 3 3h14a1 1 0 0 0 0-2H4a1 1 0 0 1-1-1z"/>
                    </svg>
                  )}
                </span>
                <div dangerouslySetInnerHTML={{ __html: getSavolText(activeQuestion) }} />
              </div>

              {isPractical ? (
                /* Monaco — редактор кода */
                <div className={s.codeEditorWrapper}>
                  <div className={s.codeEditorToolbar}>
                    <span className={s.codeEditorLabel}>
                      <span className={s.dot} style={{ background: '#ff5f56' }} />
                      <span className={s.dot} style={{ background: '#ffbd2e' }} />
                      <span className={s.dot} style={{ background: '#27c93f' }} />
                    </span>
                    <select
                      className={s.langSelect}
                      value={codeLangs[activeQuestion.id] || 'cpp'}
                      onChange={e => setCodeLangs(prev => ({ ...prev, [activeQuestion.id]: e.target.value }))}
                      disabled={submitted}
                    >
                      {CODE_LANGUAGES.map(l => (
                        <option key={l.value} value={l.value}>{l.label}</option>
                      ))}
                    </select>
                  </div>
                  <Editor
                    height="380px"
                    language={codeLangs[activeQuestion.id] || 'cpp'}
                    value={answerContents[activeQuestion.id] || ''}
                    onChange={handleCodeChange}
                    theme="vs-dark"
                    options={{
                      fontSize: 14,
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      readOnly: submitted,
                      automaticLayout: true,
                      tabSize: 4,
                      lineNumbers: 'on',
                      renderLineHighlight: 'all',
                      cursorBlinking: 'smooth',
                      smoothScrolling: true,
                      padding: { top: 12 },
                    }}
                  />
                </div>
              ) : (
                /* Tiptap — текстовый редактор */
                <>
                  {!submitted && <MenuBar />}
                  <div className={s.editorBox}>
                    <EditorContent editor={editor} />
                  </div>
                </>
              )}

              {!submitted && (
                <button className={s.submitButton} onClick={handleSubmit} disabled={isSubmitting}>
                  {isSubmitting ? '...' : (t('sendAnswer') || 'Отправить все ответы')}
                </button>
              )}
              {submitted && <p className={s.statusMessage}>{t('alreadySent') || 'Ответы отправлены'}</p>}
            </>
          ) : (
            <p>Вопросов нет.</p>
          )}
        </div>

        {/* Колонка 3: результаты */}
        <div className={s.colResults}>
          <h3 className={s.colTitle}>{t('result') || 'Результаты'}</h3>
          {Object.keys(results).length === 0 ? (
            <p className={s.noResults}>{t('waitResult') || 'Результаты появятся после проверки'}</p>
          ) : (
            <>
              <div className={s.totalScore}>
                <strong>{t('yourRating') || 'Sizning bahoyingiz'}:</strong>{' '}
                {percent}%{finalBall !== null ? `, ${finalBall} ${t('ball') || 'ball'}` : ''}
              </div>
              {/* <button
                className={s.certBtn}
                onClick={handleGetCertificate}
                disabled={certLoading}
              >
                <span>{certLoading ? '...' : (t('getCertificate') || 'Sertifikat olish')}</span>
              </button> */}
              {questions.map((q, idx) => {
                const res = results[q.id];
                if (!res) return null;
                return (
                  <div key={q.id} className={s.resultItem}>
                    <p className={s.resultLabel}>#{idx + 1} {getSavolText(q)?.slice(0, 50)}…</p>
                    {res.ball !== null && (
                      <p className={s.resultBall}>{t('yourRating') || 'Балл'}: <strong>{res.ball}</strong></p>
                    )}
                    {res.komment && (
                      <p className={s.resultComment}>{res.komment}</p>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>

      {/* Модалки */}
      {isImageModalOpen && (
        <div className={s.modalOverlay}>
          <div className={s.modal}>
            <h3>{t('enterImg')}</h3>
            <input type="text" value={imageValue} onChange={e => setImageValue(e.target.value)} placeholder={t('enterUrlImg')} />
            <div className={s.modalActions}>
              <button onClick={() => { editor.chain().focus().setImage({ src: imageValue }).run(); setImageValue(''); setImageModalOpen(false); }}>{t('enterImg')}</button>
              <button onClick={() => setImageModalOpen(false)}>{t('cansel')}</button>
            </div>
          </div>
        </div>
      )}

      {isLinkModalOpen && (
        <div className={s.modalOverlay}>
          <div className={s.modal}>
            <h3>{t('enterLink')}</h3>
            <input type="text" value={linkValue} onChange={e => setLinkValue(e.target.value)} placeholder={t('enterUrlLink')} />
            <div className={s.modalActions}>
              <button onClick={() => { editor.chain().focus().extendMarkRange('link').setLink({ href: linkValue }).run(); setLinkValue(''); setLinkModalOpen(false); }}>{t('add')}</button>
              <button onClick={() => setLinkModalOpen(false)}>{t('cansel')}</button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className={s.modalOverlay}>
          <div className={s.modal}>
            <h3>{t('sent')}</h3>
            <p>{t('enterSoon')}</p>
            <div className={s.modalActions}>
              <button className={s.submitButton} onClick={() => setShowModal(false)}>{t('close')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
